import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  getDoc,
  doc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { fetchAndActivate } from "firebase/remote-config";

// FirestoreService provides common Firestore operations
const FirestoreService = {
  // ...existing methods...
  addOrReplaceSongForToday: async (songData, userid) => {
    try {
      // Get today's date in Trinidad and Tobago timezone (America/Port_of_Spain)
      const today = new Date().toLocaleDateString("en-CA", {
        timeZone: "America/Port_of_Spain",
      }); // Format: YYYY-MM-DD

      // Reference the songs collection
      const songsCollectionRef = collection(db, "songs");

      // Query for any existing songs added by the current user for today
      const userSongQuery = query(
        songsCollectionRef,
        where("added_by", "==", userid),
        where("date", "==", today)
      );
      const querySnapshot = await getDocs(userSongQuery);

      // Delete existing songs by the user for today
      for (const docSnap of querySnapshot.docs) {
        await deleteDoc(docSnap.ref);
      }

      // Add the new song with an auto-generated ID
      const newSong = {
        song_id: songData.id,
        song_name: songData.name,
        song_album: songData.album?.images?.[0]?.url || "",
        spotify_link: songData.external_urls?.spotify || "",
        artist_name: Array.isArray(songData.artists)
          ? songData.artists.map((a) => a.name).join(", ")
          : "",
        added_at: Date.now(),
        added_by: userid,
        date: today,
      };

      const docRef = await addDoc(songsCollectionRef, newSong);

      console.log(`Song added with ID: ${docRef.id}`);
    } catch (error) {
      console.error("Error adding or replacing song:", error);
    }
  },

  // Fetch songs for today with user details, returns unsubscribe function
  fetchSongsForToday(callback, loadingCallback = () => {}) {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const day = today.getDate();

      const formattedDateShort = `${year}-${month}-${day}`;
      const formattedDateLong = `${year}-${month
        .toString()
        .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

      const songsCollectionRef = collection(db, "songs");
      const todayQuery = query(
        songsCollectionRef,
        where("date", "in", [formattedDateShort, formattedDateLong])
      );

      // Return the unsubscribe function so the caller can manage the subscription
      const unsubscribe = onSnapshot(todayQuery, async (snapshot) => {
        if (snapshot.empty) {
          loadingCallback(false);
          callback([]);
        } else {
          const fetchedSongs = await Promise.all(
            snapshot.docs.map(async (docSnapshot) => {
              const songData = docSnapshot.data();
              const addedById = songData.added_by;
              let user = null;
              if (addedById) {
                try {
                  const userDoc = await getDoc(doc(db, "users", addedById));
                  if (userDoc.exists()) {
                    user = { id: userDoc.id, ...userDoc.data() };
                  }
                } catch (error) {
                  console.error(
                    `Failed to fetch user for ID: ${addedById}`,
                    error
                  );
                }
              }
              return {
                id: docSnapshot.id,
                ...songData,
                user,
              };
            })
          );

          // Sort songs by date descending (latest first)
          fetchedSongs.sort((a, b) =>
            (b.date || "").localeCompare(a.date || "")
          );
          callback(fetchedSongs);
          loadingCallback(false);
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error fetching songs for today:", error);
      loadingCallback(false);
    }
  },

  fetchAllSongsGroupedByDate(callback, loadingCallback = () => {}) {
    try {
      const songsCollectionRef = collection(db, "songs");

      const unsubscribe = onSnapshot(songsCollectionRef, async (snapshot) => {
        if (snapshot.empty) {
          loadingCallback(false);
          callback({});
        } else {
          const fetchedSongs = await Promise.all(
            snapshot.docs.map(async (docSnapshot) => {
              const songData = docSnapshot.data();
              const addedById = songData.added_by;
              let user = null;
              if (addedById) {
                try {
                  const userDoc = await getDoc(doc(db, "users", addedById));
                  if (userDoc.exists()) {
                    user = { id: userDoc.id, ...userDoc.data() };
                  }
                } catch (error) {
                  console.error(
                    `Failed to fetch user for ID: ${addedById}`,
                    error
                  );
                }
              }
              return {
                id: docSnapshot.id,
                ...songData,
                user,
              };
            })
          );

          // Group songs by date
          const groupedByDate = {};
          fetchedSongs.forEach((song) => {
            let date = song.date || "unknown";
            // Normalize date to YYYY-MM-DD (pad month and day with zeros)
            if (date !== "unknown") {
              const parts = date.split("-");
              if (
                parts.length === 3 &&
                /^\d{4}$/.test(parts[0]) &&
                /^\d{1,2}$/.test(parts[1]) &&
                /^\d{1,2}$/.test(parts[2])
              ) {
                const year = parts[0];
                const month = parts[1].padStart(2, "0");
                const day = parts[2].padStart(2, "0");
                date = `${year}-${month}-${day}`;
              } else {
                date = "unknown";
              }
            }
            if (!groupedByDate[date]) {
              groupedByDate[date] = [];
            }
            groupedByDate[date].push(song);
          });

          // Sort the grouped dates in descending order (latest first)
          const sortedGroupedByDate = {};
          Object.keys(groupedByDate)
            .sort((a, b) => {
              // Only parse valid YYYY-MM-DD dates, otherwise treat as 0
              const isValidDate = (str) =>
                /^\d{4}-\d{2}-\d{2}$/.test(str) &&
                !isNaN(new Date(str).getTime());
              const dateA = isValidDate(a) ? new Date(a).getTime() : 0;
              const dateB = isValidDate(b) ? new Date(b).getTime() : 0;
              return dateB - dateA; // descending order
            })
            .forEach((date) => {
              // Optionally, sort songs within each date as well
              groupedByDate[date].sort((a, b) =>
                (b.date || "").localeCompare(a.date || "")
              );
              sortedGroupedByDate[date] = groupedByDate[date];
            });

          callback(sortedGroupedByDate);
          loadingCallback(false);
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error fetching all songs grouped by date:", error);
      loadingCallback(false);
    }
  },
};

export default FirestoreService;
